import api from "@/services/api";
import { formatDateToString } from "@/utils";
const { post, get, put, patch, delete: del, deleteOnUnload } = api.getPrefixedMethods("intake");

export const intakeService = {
  getIntakeDocuments({
    status,
    sortBy,
    patient,
    date,
    search,
    dateFrom,
    dateTo,
    documentType,
    tags,
    modifiedBy,
    detail,
    page = 1,
    pageSize = 25,
  }) {
    const params = { status, sort_by: sortBy, page_size: pageSize, page };
    if (search) params.search = search;
    else if (patient) params.patient = patient;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (!dateFrom && !dateTo && date) params.date = date;
    if (documentType) params.document_type = documentType;
    if (tags) params.tags = tags;
    if (modifiedBy) params.modified_by = modifiedBy;
    if (detail) params.detail = "true";
    return get("documents", { params });
  },
  getIntakeDocumentPages(documentKey) {
    return get(`documents/${documentKey}/pages`, { params: { page_size: 500 } });
  },
  getIntakeDocument(documentKey) {
    return get(`documents/${documentKey}`);
  },
  acquireIntakeDocumentLock(documentKey) {
    return post(`documents/${documentKey}/locks/`);
  },
  getIntakeDocumentLock(documentKey) {
    return get(`documents/${documentKey}/locks/`);
  },
  releaseIntakeDocumentLock(documentKey) {
    return del(`documents/${documentKey}/locks/`);
  },
  releaseIntakeDocumentLockOnUnload(documentKey) {
    return deleteOnUnload(`documents/${documentKey}/locks/`);
  },
  getDocumentLocks(documentKeys) {
    return get("combined-feed/", { params: { only: "locks", keys: documentKeys.join(",") } });
  },
  startIntakeDocumentStaffTime(documentKey) {
    return post(`documents/${documentKey}/staff-time`);
  },
  pushIntakeDocumentStaffTime(documentKey) {
    return del(`documents/${documentKey}/staff-time`);
  },
  patchIntakeDocument(documentKey, values) {
    return patch(`documents/${documentKey}`, values);
  },
  patchIntakeDocumentPage(documentKey, pageId, values) {
    return patch(`documents/${documentKey}/pages/${pageId}`, values);
  },
  bulkPatchPages(documentKey, pages) {
    return patch(`documents/${documentKey}/pages/`, { pages });
  },
  postIntakeDocumentSubmit(documentKey, values) {
    return post(`documents/${documentKey}/submit-intake`, values);
  },
  postIntakeDocumentSplit(documentKey, { pageIdsSplits }) {
    return post(`documents/${documentKey}/split/`, { pageIdsSplits });
  },
  getIntakeDocumentAuditLogs(documentKey, pagingOptions) {
    return get(`documents/${documentKey}/audits/`, { params: pagingOptions });
  },
  getPatientDocumentPages(patientId, documentType) {
    return get(`patients/${patientId}/pages/`, { params: { document_type: documentType, page_size: 1000 } });
  },
  getOrders({ status, patient, date, search, dateFrom, dateTo, tags, modifiedBy, detail, page = 1, pageSize = 25 }) {
    const params = { status, page_size: pageSize, page };
    if (search) params.search = search;
    else if (patient) params.patient = patient;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (!dateFrom && !dateTo && date) params.date = date;
    if (tags) params.tags = tags;
    if (modifiedBy) params.modified_by = modifiedBy;
    if (detail) params.detail = "true";
    return get("orders/", { params });
  },
  getIntakeOrder(orderId) {
    return get(`orders/${orderId}`);
  },
  postQuickStartClinicalReview(data) {
    return post("orders/quick-start-clinical-review/", data);
  },
  postQuickStartNetworkQuery(data) {
    return post("network-queries/", data);
  },
  getCombinedFeed({
    status,
    search,
    patient,
    dateFrom,
    dateTo,
    documentType,
    tags,
    tagsMatch,
    dueFrom,
    dueTo,
    substatus,
    substatusMatch,
    modifiedBy,
    sentBy,
    assignedUser,
    assignedGroup,
    snoozed,
    sortKey,
    sortDir,
    page = 1,
    pageSize = 20,
  } = {}) {
    const params = {
      status,
      search,
      patient: search ? undefined : patient,
      dateFrom,
      dateTo,
      documentType,
      tags,
      tagsMatch,
      dueFrom,
      dueTo,
      substatus,
      substatusMatch,
      modifiedBy,
      sentBy,
      assignedUser,
      assignedGroup,
      snoozed: snoozed ? "true" : undefined,
      sortKey: sortKey || undefined,
      sortDir: sortDir || undefined,
      page,
      pageSize,
    };
    return get("combined-feed/", { params });
  },
  getOrderAuditLogs(orderId, pagingOptions) {
    return get(`orders/${orderId}/audits/`, { params: pagingOptions });
  },
  postOrderSubmitQualify(orderId) {
    return post(`orders/${orderId}/submit-qualify`);
  },
  postUpdateOrderStatus(orderId, status) {
    return post(`orders/${orderId}/update-status/`, { status });
  },
  patchIntakeOrder(orderId, data) {
    return patch(`orders/${orderId}`, data);
  },
  postIntakeOrderPageAttach(orderId, pageId, tag) {
    return post(`orders/${orderId}/attach-page`, { page_id: pageId, tag });
  },
  deleteIntakeOrderPageAttach(orderId, pageId, tag) {
    return post(`orders/${orderId}/attach-page`, { delete: true, page_id: pageId, tag });
  },
  postOrdersSubmitClinicalReview(orderId, payload = {}) {
    return post(`orders/${orderId}/submit-clinical-review`, payload);
  },
  postDemoUpload({ link }) {
    return post("demo-upload/", { link });
  },
  patchOrderForm(orderFormId, values) {
    if ("dateOfOrder" in values && values.dateOfOrder instanceof Date) {
      values.dateOfOrder = formatDateToString(values.dateOfOrder);
    }
    return patch(`order-forms/${orderFormId}`, values);
  },
  patchToDo(toDoId, values) {
    if ("dueDate" in values && values.dueDate instanceof Date) {
      values.dueDate = formatDateToString(values.dueDate);
    }
    return patch(`to-dos/${toDoId}`, values);
  },
  patchInsuranceCard(insuranceCardId, values) {
    if ("memberDob" in values && values.memberDob instanceof Date) {
      values.memberDob = formatDateToString(values.memberDob);
    }
    return patch(`insurance-cards/${insuranceCardId}`, values);
  },
  getPatientInsuranceCards(patientId) {
    return get(`patients/${patientId}/insurance-cards/`, { params: { page_size: 100 } });
  },
  postCreateInsuranceCardFromPage(pageId) {
    return post(`pages/${pageId}/create-insurance-card/`);
  },
  postCreateOrderFormFromPage(pageId, fallbackToOtherPages = true) {
    return post(`pages/${pageId}/create-order-form/`, { fallbackToOtherPages });
  },
  postCreateToDoFromPage(pageId) {
    return post(`pages/${pageId}/create-to-do/`);
  },
  deleteInsuranceCard(insuranceCardId) {
    return del(`insurance-cards/${insuranceCardId}/`);
  },
  deleteOrderForm(orderFormId) {
    return del(`order-forms/${orderFormId}/`);
  },
  deleteToDo(toDoId) {
    return del(`to-dos/${toDoId}/`);
  },
  postSnoozeDocument(documentKey, minutes) {
    return post(`documents/${documentKey}/snooze/`, { minutes });
  },
  postSnoozeOrder(orderId, minutes) {
    return post(`orders/${orderId}/snooze/`, { minutes });
  },
  postArchiveDocument(documentKey) {
    return post(`documents/${documentKey}/archive/`);
  },
  postArchiveOrder(orderId) {
    return post(`orders/${orderId}/archive/`);
  },
  deleteArchiveDocument(documentKey) {
    return del(`documents/${documentKey}/archive/`);
  },
  deleteArchiveOrder(orderId) {
    return del(`orders/${orderId}/archive/`);
  },
  getIntakeAutomationsInfo(documentKey) {
    return get(`documents/${documentKey}/automations/`);
  },
  getPatientIntakeRecords(patientId, params = {}) {
    const { page = 1, pageSize = 5 } = params;
    return get(`patients/${patientId}/intake-records/`, {
      params: { page, page_size: pageSize },
    });
  },
  getPatientImportedOrders(patientId) {
    return get(`patients/${patientId}/imported-orders/`);
  },
  getDocumentPreview(documentId) {
    // Fetch PDF as blob following codebase pattern (handles auth automatically)
    return get(`documents/${documentId}/preview/`, {
      responseType: "blob",
      headers: { "Content-Type": "application/pdf" },
      _returnFullResponse: true,
      _skipPropsAdaptation: true,
    });
  },
  getReimbursementData(orderId, payorName, productType) {
    return get(`orders/${orderId}/reimbursement-data/`, {
      params: { payor_name: payorName, product_type: productType },
    });
  },
  getReimbursementOptions() {
    return get("reimbursement-options/");
  },
  getPolicyReview(orderId) {
    return get(`orders/${orderId}/policy-review/`);
  },
  triggerPolicyReview(orderId, data = {}) {
    return post(`orders/${orderId}/policy-review/`, data);
  },
  searchPolicies(orderId, keyword) {
    return post(`orders/${orderId}/policy-review/search/`, { keyword });
  },
  cancelPolicyReview(orderId) {
    return post(`orders/${orderId}/policy-review/cancel/`, {});
  },
  updatePolicyReview(orderId, data) {
    return patch(`orders/${orderId}/policy-review/`, data);
  },
  updateDocumentTags(documentKey, tags, tagDueDates) {
    return put(`documents/${documentKey}/tags/`, { tags, tagDueDates });
  },
  updateOrderTags(orderId, tags, tagDueDates) {
    return put(`orders/${orderId}/tags/`, { tags, tagDueDates });
  },
};
